/**
 * ContractorsListCockpit — full structural Cockpit rewrite of /contractors.
 *
 * Replaces the cosmetic CockpitSurfaceWrap pass with a native Cockpit layout
 * built from CockpitMasthead / HeroMetric / CockpitFilterBar / CockpitListShell
 * primitives. Mirrors the pattern used by CustomersListCockpit so density,
 * type scale, and rhythm stay identical across the platform.
 *
 * Business logic (filters, branch/region scoping, scorecards) is preserved
 * verbatim — only the chrome changes.
 */
import { useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, Shield, AlertTriangle } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { getOnboardingLifecycleRows } from "@/data/contractor/onboarding/lifecycle";
import { getContractorComplianceListStatus } from "@/data/contractor/compliance/contractorComplianceListStatus";
import { ContractorService } from "@/services/contractor.service";
import type { Contractor } from "@/data/types";
import { BranchRegionFilter, EMPTY_BR, type BranchRegionState } from "@/components/BranchRegionFilter";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  InlineMeta,
  StatusDot,
} from "@/components/cockpit/CockpitShell";

type FilterTab = "all" | "active" | "pending-response" | "high-rated" | "compliance-risk" | "inactive";

const TEMPLATE = "minmax(220px,1.4fr) minmax(140px,.9fr) minmax(120px,.8fr) 90px 110px 110px 24px";

export default function ContractorsListCockpit() {
  const { opportunities } = useAppData();
  const onboarding = useOnboarding();
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [filter, setFilter] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const withdrawnContractorIds = useMemo(
    () =>
      new Set(
        getOnboardingLifecycleRows(onboarding.inviteTokens, onboarding.sessions)
          .filter((row) => row.stage === "withdrawn" && row.contractorId)
          .map((row) => row.contractorId as string),
      ),
    [onboarding.inviteTokens, onboarding.sessions],
  );

  useEffect(() => {
    ContractorService.fetchContractors().then(setContractors).catch(console.warn);
    return ContractorService.subscribeToContractors(setContractors);
  }, []);

  const brFiltered = useMemo(() => contractors.filter((c) => {
    if (withdrawnContractorIds.has(c.id)) return false;
    if (brFilter.branch !== "all" && c.branch !== brFilter.branch) return false;
    if (brFilter.region !== "all" && !c.serviceRegions.includes(brFilter.region)) return false;
    return true;
  }), [contractors, brFilter, withdrawnContractorIds]);

  const contractorById = useMemo(
    () => new Map(contractors.map((contractor) => [contractor.id, contractor])),
    [contractors],
  );

  const getCompliance = useCallback(
    (contractor: Contractor) =>
      getContractorComplianceListStatus(
        contractor,
        onboarding.complianceDocuments,
        onboarding.sessions,
        contractor.parentEntityId ? contractorById.get(contractor.parentEntityId) : undefined,
      ),
    [contractorById, onboarding.complianceDocuments, onboarding.sessions],
  );

  const buckets = useMemo(() => {
    const active = brFiltered.filter((c) => c.active);
    const inactive = brFiltered.filter((c) => !c.active);
    const pendingResponse = brFiltered.filter((c) => c.active && opportunities.some((o) => o.contractorId === c.id && (o.responseStatus === "Pending" || o.responseStatus === "No Response")));
    const highRated = brFiltered.filter((c) => c.active && c.rating >= 4.5);
    const complianceRisk = brFiltered.filter((c) => c.active && getCompliance(c).tone !== "ok");
    return { active, inactive, pendingResponse, highRated, complianceRisk };
  }, [brFiltered, opportunities, getCompliance]);

  const filtered = useMemo(() => {
    let list = brFiltered;
    if (filter === "active") list = buckets.active;
    else if (filter === "inactive") list = buckets.inactive;
    else if (filter === "pending-response") list = buckets.pendingResponse;
    else if (filter === "high-rated") list = buckets.highRated;
    else if (filter === "compliance-risk") list = buckets.complianceRisk;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        c.companyName.toLowerCase().includes(q) ||
        c.contactName.toLowerCase().includes(q) ||
        c.tradeType.toLowerCase().includes(q) ||
        c.serviceArea.toLowerCase().includes(q),
      );
    }
    return list;
  }, [brFiltered, buckets, filter, search]);

  const totalOpps = opportunities.length;
  const totalWon = opportunities.filter((o) => o.isWinner).length;
  const avgResponseTime = useMemo(() => {
    const responded = opportunities.filter((o) => o.responseDate && o.sentDate);
    if (responded.length === 0) return "—";
    const totalDays = responded.reduce((sum, o) => {
      const sent = new Date(o.sentDate).getTime();
      const resp = new Date(o.responseDate).getTime();
      return sum + Math.max(0, (resp - sent) / (1000 * 60 * 60 * 24));
    }, 0);
    const avg = totalDays / responded.length;
    return avg < 1 ? "<1d" : `${avg.toFixed(1)}d`;
  }, [opportunities]);

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Installers"
        right={(
          <>
            <HeroMetric label="Active" value={buckets.active.length} hero />
            <HeroDivider />
            <HeroMetric label="Opportunities sent" value={totalOpps} />
            <HeroMetric label="Jobs awarded" value={totalWon} />
            <HeroMetric label="Avg response" value={avgResponseTime} />
          </>
        )}
      />

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search contractors" />}
        chips={(
          <CockpitChipGroup
            value={filter}
            onChange={(v) => setFilter(v as FilterTab)}
            options={[
              { value: "all", label: "All", count: brFiltered.length },
              { value: "active", label: "Active", count: buckets.active.length },
              { value: "pending-response", label: "Pending", count: buckets.pendingResponse.length },
              { value: "high-rated", label: "Top rated", count: buckets.highRated.length },
              { value: "compliance-risk", label: "Compliance", count: buckets.complianceRisk.length },
              { value: "inactive", label: "Inactive", count: buckets.inactive.length },
            ]}
          />
        )}
        right={<BranchRegionFilter value={brFilter} onChange={setBrFilter} contractors={contractors} />}
      />

      <CockpitListShell>
        <CockpitColumns template={TEMPLATE}>
          <span>Company</span>
          <span>Contact</span>
          <span>Trade</span>
          <span>Rating</span>
          <span>Compliance</span>
          <span>Service area</span>
          <span />
        </CockpitColumns>

        {filtered.length === 0 ? (
          <CockpitEmpty message="No contractors match this view." />
        ) : filtered.map((c) => {
          const compliance = getCompliance(c);
          const compliant = compliance.tone === "ok";
          return (
            <CockpitRow key={c.id} template={TEMPLATE} to={`/contractors/${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <StatusDot tone={c.active ? "good" : "muted"} />
                  <p className="text-sm font-medium text-foreground truncate">{c.companyName}</p>
                </div>
                <InlineMeta items={[c.branch || "—", c.active ? "Active" : "Inactive"]} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-foreground truncate">{c.contactName}</p>
                <p className="text-xs text-muted-foreground truncate">{c.phone}</p>
              </div>
              <p className="text-xs text-foreground truncate">{c.tradeType}</p>
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 text-warning fill-warning" />
                <span className="text-xs text-foreground tabular-nums">{c.rating.toFixed(1)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {compliant ? (
                  <>
                    <Shield className="h-3 w-3 text-success" />
                    <span className="text-xs text-muted-foreground">{compliance.label}</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3 text-warning" />
                    <span className="text-xs text-warning">{compliance.label}</span>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">{c.serviceArea || "—"}</p>
            </CockpitRow>
          );
        })}
      </CockpitListShell>
    </CockpitPage>
  );
}
